import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { ApiContext } from '@api/api';
import { CurrencyLockTypeEnum, EntityTypeEnum, FeaturesEnum, MergeInfoInDTO, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import SvgRoute from '@components/ringil3/icons/heading/Route.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import { CarrierDistributionSelectorData, DistributionType } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { useCreateUpdateExpeditionNameSidebar } from '@components/ringil3/Features/Expedition/Sidebars/UpdateExpeditionNameSidebar.tsx';
import { useCreatePrefillFromFavoritesSidebar } from '@components/ringil3/Features/Expedition/Sidebars/PrefillFromFavoritesSidebar.tsx';
import { PrefillOptions, useExpeditionPrefillDefaults } from '@components/RingilComponents/expeditionPrefillOptions.ts';
import { expeditionConfig } from '@components/framework/orders/validation.ts';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import ValidationSummaryPanel from '@components/RingilComponents/ValidationSummary/ValidationSummaryPanel.tsx';
import { buildExpeditionErrorRows } from '@app/pages/expedition/create/errorSummary.ts';
import { isLadenFactorEligibleFromLocations } from '@utils/expeditionUtils.ts';
import { createLogger } from '@utils/logger';
import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { newExpeditionInitialDataWithDefaults } from '@app/pages/expedition/create/data.ts';
import { buildExpeditionValidationSchema } from '@app/pages/expedition/create/validation.ts';
import { buildSaveExpeditionRequest, PrefilledEntity } from '@app/pages/expedition/create/mappers/formToSaveRequest.ts';
import { PrefillResult, PrefillSource, useExpeditionPrefill } from '@app/pages/expedition/create/hooks/useExpeditionPrefill.ts';
import { MAX_MERGED_EXPEDITIONS } from '@app/pages/expedition/create/mergeEligibility.ts';
import { loadConsolidationGroup, markGroupCreated } from '@app/pages/customer/supplier-notification/route-consolidation/routeConsolidationStorage.ts';
import { useRouteEditor } from '@app/pages/expedition/create/hooks/useRouteEditor.ts';
import ExpeditionHeader from '@app/pages/expedition/create/components/ExpeditionHeader.tsx';
import MergeInfoPanel from '@app/pages/expedition/create/components/MergeInfoPanel.tsx';
import MergeConfirmDialog from '@app/pages/expedition/create/components/MergeConfirmDialog.tsx';
import RouteSection from '@app/pages/expedition/create/components/RouteSection.tsx';
import AttachmentsSection from '@app/pages/attachment/AttachmentsSection.tsx';
import CarrierSection from '@app/pages/expedition/create/components/CarrierSection.tsx';
import InternalInfoSection from '@app/pages/expedition/create/components/InternalInfoSection.tsx';

const log = createLogger('createExpedition');

interface Entity {
  id: string;
  type: EntityTypeEnum;
}

interface ComponentProps {
  /** Entity to prefill from — an existing shipment or a supplier notification. */
  entity?: Entity;
  prefillOptions?: PrefillOptions;
  /** Shipments to merge into this one; the form then replaces them all. */
  mergeExpeditionIds?: string[];
  /** Supplier notifications to merge into this one, on their own or alongside shipments. */
  mergeSupplierNotificationIds?: string[];
  /** A route-optimization group to create the shipment from, as stored by the notification list. */
  consolidationRequestId?: string;
  consolidationGroupId?: number;
}

/** The prefill sources that come from a single entity, as opposed to a merge of several. */
type SingleEntityPrefillKind = Extract<PrefillSource, { id: string }>['kind'];

/** Maps the entity types this page can be opened for onto the prefill sources the hook knows. */
const PREFILL_KIND_BY_ENTITY_TYPE: Partial<Record<EntityTypeEnum, SingleEntityPrefillKind>> = {
  [EntityTypeEnum.EXPEDITION]: 'expedition',
  [EntityTypeEnum.SUPPLIER_NOTIFICATION]: 'supplierNotification',
};

const CreateExpedition: React.FC<ComponentProps> = ({
  entity,
  prefillOptions,
  mergeExpeditionIds,
  mergeSupplierNotificationIds,
  consolidationRequestId,
  consolidationGroupId,
}) => {
  const { createExpedition } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const { showToastMessage } = useContext(ToastContext);
  const { BackendValidationComponent, setBeValidationErrors, beValidationErrors } = useBEValidation();
  const { loggedUser, hasPermission, hasFeature } = useContext(AuthenticatedUserContext);
  // Beta, company-wide, no end-user switch: lists every error at the top instead of leaving the toast
  // and the red fields to be hunted down.
  const { showLegacy: errorSummaryOff } = useBetaVersion(BetaSurface.CREATE_EXPEDITION_ERROR_SUMMARY);

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Route issues are recomputed on every edit, so they are only worth listing once something has been
  // submitted — before that they are just a half-built route, not a complaint.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [originalDistribution, setOriginalDistribution] = useState<CarrierDistributionSelectorData>(null);
  // Only used to suppress the route's entry animation on the first paint.
  const [hasMounted, setHasMounted] = useState(false);
  // Present only when this form replaces other shipments.
  const [merge, setMerge] = useState<PrefillResult['merge']>(null);
  // Where the form content came from: the route entity, or the last "Prefill from favorites" pick that replaced it.
  const [prefilledEntity, setPrefilledEntity] = useState<PrefilledEntity>(entity ? { ...entity, origin: 'ROUTE' } : null);
  const [mergeConfirmVisible, setMergeConfirmVisible] = useState(false);

  const editDistributionAllowed = hasPermission([
    PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW,
    PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW,
    PermissionTypesEnum.PERMISSION_CARRIER_GROUP_READ,
  ]);
  const currencyLockEnabled = hasFeature([FeaturesEnum.CURRENCY_LOCK]);
  const ordersConfig = expeditionConfig(!hasFeature([FeaturesEnum.NOT_MANDATORY_DIMENSIONS]));

  const { UpdateExpeditionNameSidebar, setVisible: setUpdateNameVisible } = useCreateUpdateExpeditionNameSidebar();
  const { PrefillFromFavoritesSidebar, setVisible: setPrefillFromFavoritesVisible } = useCreatePrefillFromFavoritesSidebar(editDistributionAllowed);

  // RD-2977: whether the company requires a folder on every shipment (module settings)
  const prefillDefaults = useExpeditionPrefillDefaults({ refresh: true });
  const folderRequired = prefillDefaults?.folderRequired ?? false;

  const form = useForm<NewExpeditionForm>(
    buildExpeditionValidationSchema({ tr, ordersConfig, ladenFactorEnabled: hasFeature([FeaturesEnum.LADEN_FACTOR]), folderRequired, defaultTimezoneId: loggedUser?.defaultTimezoneId }),
    newExpeditionInitialDataWithDefaults(),
    d => save(d),
    false,
    false,
  );

  const route = useRouteEditor(form);
  const { settings, prefill } = useExpeditionPrefill({ editDistributionAllowed });

  // `useForm` hands back a fresh object every render, so callbacks closing over `form` directly can
  // never be stable. Reading it through a ref lets memoised children keep their identity.
  const formRef = useRef(form);
  formRef.current = form;

  const setAttachments = useCallback((attachments: NewExpeditionForm['attachments']) => {
    formRef.current.setFieldValue('attachments', attachments);
  }, []);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    log.debug('Form validation errors:', form.validationErrors);
  }, [form?.validationErrors]);

  const showLoadErrorToast = () => {
    showToastMessage(tr('CreateExpedition.error', 'Chyba'), tr('CreateExpedition.expeditionNotFound', 'Expedici se nepodařilo načíst'), 'error');
    nav.navigate(nav.urlFunctions.createExpedition());
  };

  const applyPrefill = async (source: PrefillSource, onFailed: () => void) => {
    const result = await prefill(source);
    if (!result) {
      onFailed();
      return;
    }
    form.overwriteValues(result.values);
    if (result.originalDistribution !== undefined) setOriginalDistribution(result.originalDistribution);
    setMerge(result.merge ?? null);
  };

  // Prefill from whatever the page was opened for. The hook waits for the company's distribution
  // settings itself, so there is nothing to sequence here.
  useEffect(() => {
    if (!entity) return;

    const kind = PREFILL_KIND_BY_ENTITY_TYPE[entity.type];
    if (!kind) {
      showLoadErrorToast();
      return;
    }

    setPrefilledEntity({ ...entity, origin: 'ROUTE' });
    applyPrefill({ kind, id: entity.id, options: prefillOptions }, showLoadErrorToast);
  }, [entity?.id, entity?.type]);

  // Opened from a list's merge action: fold the selected entities into this form. Either list may
  // be empty — a merge can start from the shipment list, from the notification list, or from a link
  // carrying both — so the cap applies to the two of them together.
  useEffect(() => {
    const expeditionIds = mergeExpeditionIds ?? [];
    const supplierNotificationIds = mergeSupplierNotificationIds ?? [];
    if (expeditionIds.length + supplierNotificationIds.length < 2) return;

    applyPrefill(
      {
        kind: 'merge',
        expeditionIds: expeditionIds.slice(0, MAX_MERGED_EXPEDITIONS),
        supplierNotificationIds: supplierNotificationIds.slice(0, MAX_MERGED_EXPEDITIONS),
      },
      showLoadErrorToast,
    );
  }, [mergeExpeditionIds?.join(','), mergeSupplierNotificationIds?.join(',')]);

  // Opened from a route optimization: the same merge, on the optimizer's route. The backend keeps no
  // result, so a group missing from this tab's storage (a new tab, cleared site data) cannot be
  // fetched again — the user is told to rerun the optimization and gets a blank form.
  useEffect(() => {
    if (!consolidationRequestId || consolidationGroupId === undefined) return;

    const stored = loadConsolidationGroup(consolidationRequestId, consolidationGroupId);
    if (!stored) {
      showToastMessage(
        tr('RouteConsolidation.groupMissingTitle', 'Optimization not found'),
        tr('RouteConsolidation.groupMissing', 'The route optimization result is no longer available. Run the optimization again on the supplier notification list.'),
        'warn',
      );
      return;
    }

    applyPrefill(
      {
        kind: 'consolidation',
        group: stored.group,
        consolidationRequestId: stored.consolidationRequestId,
        modelVersion: stored.modelVersion,
        removedSupplierNotificationIds: stored.removedSupplierNotificationIds,
      },
      showLoadErrorToast,
    );
  }, [consolidationRequestId, consolidationGroupId]);

  /**
   * Drops one entity from the merge and rebuilds the form from those that remain. Below two there
   * is nothing to merge, so the page reverts to an ordinary blank create form.
   */
  const removeMergeSource = (sourceId: string) => {
    const remaining = merge.sources.filter(source => source.id !== sourceId);
    if (remaining.length < 2) {
      form.overwriteValues(newExpeditionInitialDataWithDefaults());
      setOriginalDistribution(null);
      setMerge(null);
      return;
    }
    applyPrefill(
      {
        kind: 'merge',
        expeditionIds: remaining.filter(source => source.kind === 'expedition').map(source => source.id),
        supplierNotificationIds: remaining.filter(source => source.kind === 'supplierNotification').map(source => source.id),
      },
      showLoadErrorToast,
    );
  };

  const initDistribution = (): CarrierDistributionSelectorData => {
    const ladenFactorEligible = isLadenFactorEligibleFromLocations(form.values.locations, hasFeature([FeaturesEnum.LADEN_FACTOR]));

    return {
      // RD-2978: a shipment from a Linka goes only to its contracted carriers
      distributionType: form.values.tmplV2Id ? DistributionType.CONTRACTED : DistributionType.SPOT,
      ladenFactor: ladenFactorEligible ? true : undefined,
      allowPartialDelivery: ladenFactorEligible ? true : undefined,
      selectedItems: [],
      responseDeadlineMode: settings.deadlineMode,
      responseDeadlineOffsetValue: settings.distributionHours,
      responseDeadlineOffsetUnit: 'hours',
      responseDeadlineLocalTime: settings.deadlineMode === 'absolute' ? dayjs().add(settings.distributionHours, 'hour') : undefined,
      responseDeadlineTimezone: loggedUser.defaultTimezoneId,
      price: { currency: loggedUser?.defaultCurrencyIsoCode, price: 0 },
      notifyAssignedCarrier: false,
      currencyLockType: currencyLockEnabled ? CurrencyLockTypeEnum.ONLY_SELECTED : CurrencyLockTypeEnum.NONE,
    };
  };

  /**
   * A shipment whose orders are not each loaded once and unloaded once afterwards cannot be
   * created, so that is checked before the form's own validation runs. The reasons are already on
   * screen next to the offending orders; the toast just says why nothing happened.
   */
  const onCreate = () => {
    setSubmitAttempted(true);
    if (route.orderIssues.length > 0) {
      showToastMessage(
        tr('CreateExpedition.routeIssuesToastTitle', 'Chyba'),
        tr('CreateExpedition.routeIssuesToastMessage', 'Some orders are not loaded and unloaded correctly. Fix the route before creating the shipment.'),
        'error',
      );
      return;
    }
    form.validateAndSend();
  };

  /**
   * Reached only once the form validates.
   *
   * A merge closes other people's shipments, so it stops here and asks first; the confirmed answer
   * comes back through {@link submit}.
   */
  const save = (data: NewExpeditionForm) => {
    if (merge) {
      setMergeConfirmVisible(true);
      return;
    }
    submit(data);
  };

  const submit = (data: NewExpeditionForm, mergeInfo?: MergeInfoInDTO) => {
    setIsSubmitting(true);
    createExpedition(
      buildSaveExpeditionRequest(data, {
        prefilledEntity,
        prefillOptions,
        defaultTimezoneId: loggedUser.defaultTimezoneId,
        mergeInfo,
      }),
      d => {
        // The notification list shows this group as done when the user goes back to it.
        if (merge?.consolidation)
          markGroupCreated(merge.consolidation.requestId, merge.consolidation.groupId, { expeditionId: d.expeditionId, applicationId: d.applicationId });
        nav.navigate(nav.urlFunctions.createExpeditionDetailV2(d.expeditionId));
        showToastMessage(
          tr('generic.success', 'Success'),
          tr('CreateExpedition.expeditionCreated', 'Expedition {{applicationId}} was created', { applicationId: d.applicationId }),
          'success',
        );
        setIsSubmitting(false);
        setMergeConfirmVisible(false);
      },
      null,
      {
        onValidationFailed: errors => {
          setBeValidationErrors(errors);
          setIsSubmitting(false);
          setMergeConfirmVisible(false);
        },
        onError: (_error, showStandardError) => {
          showStandardError();
          setIsSubmitting(false);
          setMergeConfirmVisible(false);
        },
      },
    );
  };

  return (
    <div className={'flex flex-col gap-2'}>
      <BreadCrumbComponent items={[{ label: tr('AllExpedition.allExpeditions', 'Všechny expedice'), url: nav.createNavigationLink(nav.urlFunctions.createCustomerExpeditionList()) }]} />
      {/* Distribution errors are rendered inside the carrier section, next to what they refer to. The
          summary lists the rest itself, so the standalone backend box only appears without it. */}
      {errorSummaryOff ? (
        <BackendValidationComponent validationMessageFilter={error => !error.path?.startsWith('distribution')} />
      ) : (
        <ValidationSummaryPanel
          rows={buildExpeditionErrorRows(
            {
              validationErrors: form.validationErrors,
              routeIssues: submitAttempted ? route.orderIssues : [],
              backendErrors: beValidationErrors?.filter(error => !error.path?.startsWith('distribution')),
              values: form.values,
            },
            tr,
          )}
        />
      )}

      {form.values.tmplV2Name && (
        <div className={'rounded bg-content-quaternary px-4 py-3 text-sm text-content-secondary'} data-testid={'prefilledFromLineBanner'}>
          {/* the contracted carriers are prefilled only with CONTRACTED_CARRIERS and the right to edit the distribution */}
          {form.values.distribution?.distributionType === DistributionType.CONTRACTED
            ? tr(
                'CreateExpedition.prefilledFromLineBanner',
                'Předvyplněno z linky {{name}}. Smluvní dopravce můžete upravit – změny platí jen pro tuto přepravu.',
                { name: form.values.tmplV2Name },
              )
            : tr('CreateExpedition.prefilledFromLineBannerNoCarriers', 'Předvyplněno z linky {{name}}.', { name: form.values.tmplV2Name })}
        </div>
      )}

      <ExpeditionHeader
        form={form}
        loggedUser={loggedUser}
        showSupplierNotificationLink={prefillOptions?.linkToSN}
        onEditName={() => setUpdateNameVisible(true)}
        onPrefillFromFavorites={() => setPrefillFromFavoritesVisible(true)}
      />

      {merge && (
        <MergeInfoPanel
          sources={merge.sources}
          conflicts={merge.conflicts}
          keptApart={merge.keptApart}
          contactConflicts={merge.contactConflicts}
          summary={merge.summary}
          consolidation={merge.consolidation}
          timeslotConflicts={merge.timeslotConflicts}
          onRemoveSource={removeMergeSource}
          // An optimized group is merged as computed; dropping a notification would leave its stops on the route.
          canRemoveSource={!merge.consolidation && merge.sources.length > 2}
        />
      )}

      <SectionPanel icon={<SvgRoute />} heading={tr('CreateExpedition.trasa', 'Route')}>
        <RouteSection
          form={form}
          ordersConfig={ordersConfig}
          companyId={loggedUser.companyId}
          onAddStopAbove={route.addStopAbove}
          onAddStopAtEnd={route.addStopAtEnd}
          onDeleteLocation={route.deleteLocation}
          canDeleteLocation={route.canDeleteLocation}
          onMergeStop={merge ? route.mergeStop : undefined}
          warnTimeslotOrder={!!merge?.consolidation}
          onDeleteUnloading={route.deleteUnloadingLocation}
          getUnloadableOrders={route.getAllUnloadableOrders}
          onUnloadOrder={route.unloadOrder}
          onOrderAdded={route.syncOrdersToLastLocations}
          onMoveStop={route.moveStop}
          onMoveOrderLoading={route.moveOrderLoading}
          orderIssues={route.orderIssues}
          animateEntry={hasMounted}
        />

        <AttachmentsSection attachments={form.values.attachments} onChange={setAttachments} />

        <HorizontalLine variant={'tertiary'} />

        {editDistributionAllowed && (
          <CarrierSection
            form={form}
            originalDistribution={originalDistribution}
            onCreateDistribution={() => form.setFieldValue(form.names.distribution, initDistribution())}
            hasFeature={hasFeature}
            backendValidation={<BackendValidationComponent validationMessageFilter={error => error.path?.startsWith('distribution')} />}
          />
        )}

        <InternalInfoSection form={form} folderRequired={folderRequired} />

        <div className={'flex flex-row mx-4 my-3'}>
          <RingilButton
            mode="filled"
            size="big"
            label={tr('CreateExpedition.vytvorit', 'Create')}
            onClick={onCreate}
            loading={isSubmitting}
            dataTestId="createExpedition"
          />
        </div>
      </SectionPanel>

      {merge && (
        <MergeConfirmDialog
          visible={mergeConfirmVisible}
          sources={merge.sources}
          summary={merge.summary}
          conflicts={merge.conflicts}
          keptApart={merge.keptApart}
          contactConflicts={merge.contactConflicts}
          timeslotConflicts={merge.timeslotConflicts}
          consolidation={merge.consolidation}
          hasLinkedReservations={merge.hasLinkedReservations}
          hasLinkedSupplierNotifications={merge.hasLinkedSupplierNotifications}
          loading={isSubmitting}
          onHide={() => setMergeConfirmVisible(false)}
          onConfirm={mergeInfo => submit(form.values, mergeInfo)}
        />
      )}

      <UpdateExpeditionNameSidebar currentName={form.values.name} onContinue={name => form.setFieldValue('name', name)} />
      <PrefillFromFavoritesSidebar
        onSelect={(entityId, options, kind) => {
          // The pick replaces the route source — the form now holds that expedition, so it is the one audited.
          // A supplier-notification source is kept, otherwise the shipment would lose its SN link. Templates are not audited;
          // a line is, by the backend, through the tmplV2Id the form now carries — an earlier duplicate or favourite source
          // no longer applies (otherwise the save would audit "duplicated from" and copy that expedition's attachments).
          if (kind === 'expedition' && prefilledEntity?.type !== EntityTypeEnum.SUPPLIER_NOTIFICATION) {
            setPrefilledEntity({ id: entityId, type: EntityTypeEnum.EXPEDITION, origin: 'FAVORITES' });
          } else if (kind === 'line' && prefilledEntity?.type === EntityTypeEnum.EXPEDITION) {
            setPrefilledEntity(null);
          }
          applyPrefill({ kind, id: entityId, options }, () => {});
        }}
      />
    </div>
  );
};

export default CreateExpedition;
